import { useEffect, useState } from "react";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import apiClient from "@/services/apiClient";
import DataListPage from "./DataListPage";
import ToastHelper from "@/utils/toastHelper";
import { getStaffIdentity, STAFF_ACCOUNT_TYPE } from "@/utils/staffAuth";


const STATUS_STYLES = {
  Active: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  Completed: "bg-blue-50 text-blue-700 border border-blue-200",
  Pending: "bg-amber-50 text-amber-700 border border-amber-200",
  Suspended: "bg-rose-50 text-rose-700 border border-rose-200",
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-AU");
};

const normalizeStudents = (payload) => {
  const list = Array.isArray(payload)
    ? payload
    : payload?.data ?? payload?.result ?? payload?.items ?? payload?.students ?? [];

  return list.map((student, index) => {
    const fullName = [student.firstName, student.lastName]
      .filter(Boolean)
      .join(" ");

    return {
      id: student.id ?? student.studentId ?? student.student_id ?? `S-${index + 1}`,
      studentCode: student.studentCode ?? student.id ?? "",
      name: student.name ?? (fullName || "Unknown"),
      email: student.email ?? student.emailAddress ?? "",
      phone: student.phone ?? student.phoneNumber ?? "",
      usi: student.usiNumber ?? "",
      enrolments: student.enrolments ?? student.enrollmentCount ?? 0,
      status: student.status ?? student.studentStatus ?? "Active",
      joined: formatDate(student.joined ?? student.createdAt ?? student.createdDate),
    };
  });
};

export default function AdminStudents() {
  const isSuperAdmin = getStaffIdentity().accountTypeId === STAFF_ACCOUNT_TYPE.SUPER_ADMIN;
  const [rows, setRows] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentToDelete, setStudentToDelete] = useState(null);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const response = await apiClient.get("/AdminStudent/GetStudents");
        setRows(normalizeStudents(response.data));
      } catch (err) {
        console.error("Failed to load students", err);
        setRows([]);
        ToastHelper.apiError(err, "Unable to load students");
      }
    };

    fetchStudents();
  }, []);

  const handleStudentDeleted = (student) => {
    setRows((current) => current.filter((row) => row.id !== student.id));
    setStudentToDelete(null);
    window.dispatchEvent(new CustomEvent("notifications:updated"));
  };

  return (<>
    <DataListPage
      title="Students"
      subtitle="Manage all student accounts"
      testidPrefix="students"
      rows={rows}
      statusStyles={STATUS_STYLES}
      actions={[
        { label: "View", fn: (student) => setSelectedStudent(student) },
        ...(isSuperAdmin ? [{ label: "Delete", fn: (student) => setStudentToDelete(student), destructive: true }] : []),
      ]}
      extraFilter={(r, f) => (f === "active" ? r.status === "Active" : f === "completed" ? r.status === "Completed" : true)}
      columns={[
        { key: "id", label: "ID" },
        { key: "name", label: "Name", render: (r) => <span className="font-semibold">{r.name}</span> },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "usi", label: "USI" },
        { key: "enrolments", label: "Enrolments", align: "right" },
        { key: "status", label: "Status", type: "status" },
        { key: "joined", label: "Joined" },
      ]}
    />
    {selectedStudent && <StudentViewDialog student={selectedStudent} onClose={() => setSelectedStudent(null)} />}
    {isSuperAdmin && studentToDelete && (
      <DeleteStudentDialog
        student={studentToDelete}
        onClose={() => setStudentToDelete(null)}
        onDeleted={handleStudentDeleted}
      />
    )}
  </>);
}

function StudentViewDialog({ student, onClose }) {
  const fields = [["Student ID", student.id], ["Name", student.name], ["Email", student.email], ["Mobile", student.phone], ["USI", student.usi], ["Enrolments", student.enrolments], ["Status", student.status], ["Joined", student.joined]];
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4" onMouseDown={(event) => event.target === event.currentTarget && onClose()}><div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl"><div className="flex items-center justify-between border-b px-6 py-4"><h2 className="text-xl font-display font-extrabold text-[#0A2540]">Student details</h2><button className="text-2xl text-slate-400" onClick={onClose}>×</button></div><div className="grid gap-x-6 gap-y-4 p-6 sm:grid-cols-2">{fields.map(([label, value]) => <div key={label}><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div><div className="mt-1 break-words text-sm text-slate-800">{value || "-"}</div></div>)}</div><div className="flex justify-end border-t px-6 py-4"><button className="wc-btn-primary" onClick={onClose}>Close</button></div></div></div>;
}

function DeleteStudentDialog({ student, onClose, onDeleted }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);

  const deleteStudent = async (event) => {
    event.preventDefault();
    if (!password.trim() || deleting) return;

    setDeleting(true);
    setError("");

    try {
      const response = await apiClient.post("/AdminStudent/DeleteStudent", {
        studentId: Number(student.id),
        currentPassword: password,
      });

      ToastHelper.success(response?.data?.message || "Student record deleted successfully.");
      setPassword("");
      onDeleted(student);
    } catch (err) {
      const message = err?.response?.data?.message || "Unable to delete student record.";
      setError(message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4"
      onMouseDown={(event) => !deleting && event.target === event.currentTarget && onClose()}
      data-testid="delete-student-dialog"
    >
      <form onSubmit={deleteStudent} className="w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-start gap-3 border-b px-6 py-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-600">
            <Trash2 className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-display font-extrabold text-[#0A2540]">Delete student record?</h2>
            <p className="mt-1 text-sm text-slate-500">This permanently deletes the student and all related records. This action cannot be undone.</p>
          </div>
          <button type="button" disabled={deleting} className="text-2xl leading-none text-slate-400 disabled:opacity-50" onClick={onClose}>×</button>
        </div>

        <div className="space-y-5 px-6 py-5">
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
              <div className="text-sm text-rose-800">
                <div className="font-semibold">Permanent deletion</div>
                <div className="mt-1">{student.name} · {student.email || `Student ID ${student.id}`}</div>
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="delete-student-password" className="mb-1.5 block text-sm font-semibold text-[#0A2540]">
              Enter your admin password to confirm
            </label>
            <input
              id="delete-student-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                if (error) setError("");
              }}
              className="wc-input"
              placeholder="Admin password"
              autoFocus
              disabled={deleting}
              data-testid="delete-student-password"
            />
            <p className="mt-2 text-xs text-slate-500">Your password is verified securely by the server before deletion proceeds.</p>
          </div>

          {error && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t bg-slate-50 px-6 py-4">
          <button type="button" className="wc-btn-outline" onClick={onClose} disabled={deleting}>Cancel</button>
          <button
            type="submit"
            disabled={!password.trim() || deleting}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="confirm-delete-student"
          >
            {deleting ? <><Loader2 className="h-4 w-4 animate-spin" /> Deleting...</> : <><Trash2 className="h-4 w-4" /> Delete student</>}
          </button>
        </div>
      </form>
    </div>
  );
}
